import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Avatar,
  Chip,
  Paper,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckIcon from '@mui/icons-material/Check';
import MicIcon from '@mui/icons-material/MicOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { toTitleCase } from '../../../utils/lead_helper_v2';
import { useLead } from '../../../contexts/LeadContext';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';
import EmptyNotesCard from '../../../components/common/EmptyNotesCard';
import { useModal } from '../../../contexts/ModalContext';
import AudioPlayer from '../../../components/common/AudioPlayer';
import { LeadTabPanelLoader } from './components/LeadTabPanelLoader';

// ─── helpers ────────────────────────────────────────────────────────────────

const formatAudioDuration = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const getInitials = (name = '') =>
  name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

/** Parse note_date string → JS Date (returns null on failure) */
const parseNoteDate = (dateStr = '') => {
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isToday = (dateStr) => {
  const d = parseNoteDate(dateStr);
  return d ? isSameDay(d, new Date()) : false;
};

/** Format a date string as a section header label */
const formatSectionLabel = (dateStr) => {
  const d = parseNoteDate(dateStr);
  if (!d) return dateStr;
  const today = new Date();
  if (isSameDay(d, today)) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

/** Group notes by calendar day, preserving original order */
const groupNotesByDate = (notes = []) => {
  const groups = [];
  const seen = new Map();
  notes.forEach((note) => {
    const d = parseNoteDate(note.note_date);
    const key = d
      ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
      : note.note_date;
    if (!seen.has(key)) {
      seen.set(key, groups.length);
      groups.push({ dateKey: key, label: formatSectionLabel(note.note_date), notes: [] });
    }
    groups[seen.get(key)].notes.push(note);
  });
  return groups;
};

// ─── avatar colour cycling ────────────────────────────────────────────────

const AVATAR_COLORS = [
  { bg: '#1D9E75', color: '#9FE1CB' },
  { bg: '#534AB7', color: '#CECBF6' },
  { bg: '#185FA5', color: '#B5D4F4' },
  { bg: '#D85A30', color: '#F5C4B3' },
  { bg: '#BA7517', color: '#FAC775' },
];

const authorColorMap = new Map();
let colorIdx = 0;
const getAuthorColor = (name = '') => {
  if (!authorColorMap.has(name)) {
    authorColorMap.set(name, AVATAR_COLORS[colorIdx % AVATAR_COLORS.length]);
    colorIdx++;
  }
  return authorColorMap.get(name);
};

// ─── DateSeparator ───────────────────────────────────────────────────────────

const DateSeparator = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2.5 }}>
    <Box sx={{ flex: 1, height: '1px', bgcolor: '#E5E7EB' }} />
    <Typography
      sx={{
        fontSize: '0.68rem',
        fontWeight: 700,
        color: '#9CA3AF',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: '#E5E7EB' }} />
  </Box>
);

// ─── Note accent colours ──────────────────────────────────────────────────

const NOTE_THEME = {
  ai:      { accent: '#6366F1', bg: '#EEF2FF', border: '#C7D2FE', iconBg: '#EEF2FF', iconColor: '#6366F1' },
  today:   { accent: '#10B981', bg: '#F0FDF4', border: '#A7F3D0', iconBg: '#F0FDF4', iconColor: '#10B981' },
  default: { accent: '#94A3B8', bg: '#F8FAFC', border: '#E2E8F0', iconBg: '#F8FAFC', iconColor: '#64748B' },
};

const getNoteTheme = (isAI, isToday) => {
  if (isAI) return NOTE_THEME.ai;
  if (isToday) return NOTE_THEME.today;
  return NOTE_THEME.default;
};

// ─── NoteCard ────────────────────────────────────────────────────────────────

const NoteCard = ({ note, onEdit, onDelete }) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const today = isToday(note.note_date);
  const isAI = Boolean(note.is_ai_generated);
  const hasSummary = Boolean(note?.short_summary?.trim());
  const hasAudio = Boolean(note?.audio_note_url);
  const audioDurationLabel = formatAudioDuration(note?.audio_note_duration);

  const authorName = toTitleCase(note.created_by?.name) || 'Unknown';
  const initials = note.created_by?.initials || getInitials(authorName);
  const { bg: avatarBg, color: avatarColor } = getAuthorColor(authorName);

  const theme = getNoteTheme(isAI, today);

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        mb: 1.5,
        border: '1px solid',
        borderColor: '#E9EAEC',
        borderRadius: '14px',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)',
        transition: 'box-shadow 0.2s ease, transform 0.15s ease, border-color 0.18s',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '4px',
          background: theme.accent,
          borderRadius: '14px 0 0 14px',
        },
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
          transform: 'translateY(-1px)',
          borderColor: theme.border,
          '& .note-hover-actions': { opacity: 1, pointerEvents: 'auto' },
        },
      }}
    >
      <Box sx={{ pl: 2.5, pr: 1.5, py: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>

        {/* Lead avatar: author initials for manual notes; type icon for AI / voice */}
        <Avatar
          sx={{
            width: 38,
            height: 38,
            fontSize: '0.75rem',
            fontWeight: 700,
            flexShrink: 0,
            ...(isAI || hasAudio
              ? {
                  bgcolor: theme.iconBg,
                  color: theme.iconColor,
                  border: `1.5px solid ${theme.border}`,
                  boxShadow: `0 2px 8px ${theme.accent}20`,
                }
              : {
                  bgcolor: avatarBg,
                  color: avatarColor,
                  border: `1.5px solid ${theme.border}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }),
          }}
        >
          {isAI ? (
            <SmartToyIcon sx={{ fontSize: '1rem' }} />
          ) : hasAudio ? (
            <MicIcon sx={{ fontSize: '1rem' }} />
          ) : (
            initials
          )}
        </Avatar>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* Title row: type badge + chips */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.875rem',
                color: '#0F172A',
                letterSpacing: '-0.01em',
                lineHeight: 1.3,
              }}
            >
              {authorName}
            </Typography>

            {isAI && (
              <Chip
                icon={<SmartToyIcon />}
                label="AI"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  bgcolor: '#EEF2FF',
                  color: '#4F46E5',
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-icon': { color: '#4F46E5', fontSize: '0.8rem', ml: 0.5 },
                }}
              />
            )}

            {today && (
              <Chip
                label="Today"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  bgcolor: '#F0FDF4',
                  color: '#16A34A',
                  border: '1px solid #BBF7D0',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
          </Box>

          {/* Note body */}
          <Typography
            sx={{
              fontSize: '0.82rem',
              color: '#334155',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              mb: 1.25,
            }}
          >
            {note.note}
          </Typography>

          {/* AI Summary */}
          {hasSummary && (
            <Box
              sx={{
                mb: 1.25,
                px: 1.5,
                py: 1,
                borderRadius: '8px',
                bgcolor: '#EEF2FF',
                border: '1px solid #C7D2FE',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  color: '#4338CA',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <SmartToyIcon sx={{ fontSize: '0.75rem' }} />
                AI Summary
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#3730A3', lineHeight: 1.55 }}>
                {note.short_summary}
              </Typography>
            </Box>
          )}

          {/* Audio player */}
          {hasAudio && (
            <Box sx={{ mb: 1.25 }}>
              <AudioPlayer audioUrl={note.audio_note_url} duration={audioDurationLabel} />
            </Box>
          )}

          {/* Meta row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: '#94A3B8', fontSize: '0.72rem', fontWeight: 500 }}>
            <CalendarTodayIcon sx={{ fontSize: '0.75rem' }} />
            {note.note_date} · {note.note_time}
          </Box>
        </Box>

        {/* Hover actions */}
        <Box
          className="note-hover-actions"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.15s ease',
            flexShrink: 0,
            mt: '-2px',
          }}
        >
          <Tooltip title="Edit note" placement="left">
            <IconButton
              size="small"
              onClick={() => onEdit(note)}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                color: '#64748B',
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                '&:hover': { bgcolor: '#F1F5F9', color: '#0F172A', borderColor: '#CBD5E1' },
              }}
            >
              <EditIcon sx={{ fontSize: '0.85rem' }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete note" placement="left">
            <IconButton
              size="small"
              onClick={() => setConfirmingDelete(true)}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                color: '#64748B',
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                '&:hover': { bgcolor: '#FEF2F2', color: '#EF4444', borderColor: '#FECACA' },
              }}
            >
              <DeleteIcon sx={{ fontSize: '0.85rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Inline delete confirmation ── */}
      {confirmingDelete && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(4px)',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            zIndex: 2,
          }}
        >
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
            Delete this note?
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setConfirmingDelete(false)}
            sx={{
              fontSize: '0.78rem',
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              py: 0.5,
              borderRadius: '8px',
              borderColor: '#E2E8F0',
              color: '#475569',
              '&:hover': { bgcolor: '#F1F5F9', borderColor: '#CBD5E1' },
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => { setConfirmingDelete(false); onDelete(note); }}
            sx={{
              fontSize: '0.78rem',
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              py: 0.5,
              borderRadius: '8px',
              bgcolor: '#EF4444',
              boxShadow: '0 2px 8px rgba(239,68,68,0.35)',
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            Delete
          </Button>
        </Box>
      )}
    </Paper>
  );
};

// ─── LeadNotes (main) ────────────────────────────────────────────────────────

const LeadNotes = ({ leadId }) => {
  const [noteText, setNoteText] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  const { addLeadNotes } = useLead();
  const {
    notes: currentNotes,
    updateLeadNote,
    deleteLeadNote,
    fetchLeadNotes,
    loading: ldLoading,
  } = useLeadDetails();

  const loadingNotes = ldLoading?.notes ?? false;
  const showLoader = loadingNotes && (!currentNotes || currentNotes.length === 0);

  const { openSuccessModal, openErrorModal } = useModal();

  // Group notes by date
  const noteGroups = useMemo(() => groupNotesByDate(currentNotes || []), [currentNotes]);

  // Count today's notes
  const todayCount = useMemo(
    () => (currentNotes || []).filter((n) => isToday(n.note_date)).length,
    [currentNotes]
  );

  // ── Save / Update ──
  const handleSaveNote = async () => {
    try {
      if (editMode && editingNote) {
        const response = await updateLeadNote(leadId, editingNote.id, { notes: noteText.trim() });
        if (response) {
          await fetchLeadNotes(leadId);
          resetCompose();
          openSuccessModal('Note Updated', 'Note successfully updated.');
        }
      } else {
        const response = await addLeadNotes({
          lead_ids: [parseInt(leadId)],
          notes: noteText.trim(),
        });
        if (response.status === 200) {
          await fetchLeadNotes(parseInt(leadId));
          resetCompose();
          openSuccessModal('Note Added', 'Note successfully added to the lead.');
        } else {
          openErrorModal('Failed to add note', 'Failed to add note to the lead.');
        }
      }
    } catch (error) {
      console.error('Error saving note:', error);
      openErrorModal('Error', 'An unexpected error occurred while saving the note.');
    }
  };

  const handleEdit = (note) => {
    setEditMode(true);
    setEditingNote(note);
    setNoteText(note.note);
    const el = document.getElementById('note-compose');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDelete = async (note) => {
    try {
      const response = await deleteLeadNote(leadId, note.id);
      if (response.status === 200) {
        await fetchLeadNotes(leadId);
        openSuccessModal('Note Deleted', 'Note successfully deleted.');
      } else {
        openErrorModal('Failed to delete note', 'Failed to delete the note. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      openErrorModal('Error', 'An unexpected error occurred while deleting the note.');
    }
  };

  const resetCompose = () => {
    setNoteText('');
    setEditMode(false);
    setEditingNote(null);
  };

  // ── Render ──
  return (
    <Box sx={{ px: 2.5, py: 2.5 }}>

      {/* ── Header row ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0F172A', letterSpacing: '-0.02em' }}>
            Notes
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontWeight: 500, lineHeight: 1.45 }}>
            {(currentNotes || []).length} note{(currentNotes || []).length !== 1 ? 's' : ''}
            {todayCount > 0 && ` · ${todayCount} today`}
          </Typography>
        </Box>
      </Box>

      {/* ── Compose card ── */}
      <Paper
        id="note-compose"
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: editMode ? '#10B981' : '#E9EAEC',
          borderRadius: '14px',
          bgcolor: '#FFFFFF',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          '&:focus-within': {
            borderColor: '#10B981',
            boxShadow: '0 0 0 3px rgba(16,185,129,0.1), 0 1px 3px rgba(0,0,0,0.04)',
          },
        }}
      >
        {/* Compose header */}
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            borderBottom: '1px solid #F1F5F9',
          }}
        >
          <Avatar sx={{ width: 28, height: 28, fontSize: '0.625rem', fontWeight: 700, bgcolor: '#10B981', color: '#FFF' }}>
            A
          </Avatar>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>
            {editMode ? 'Edit note' : (
              <>
                Add a note{' '}
                <Box component="span" sx={{ fontWeight: 400, color: '#94A3B8' }}>
                  — visible to your team
                </Box>
              </>
            )}
          </Typography>
        </Box>

        {/* Textarea */}
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder={
            editMode
              ? 'Edit your note here…'
              : 'Write your note here. Include important details, follow-up actions, or any relevant information…'
          }
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          sx={{
            px: 2,
            py: 1.25,
            '& .MuiInputBase-root': { fontSize: '0.84rem', lineHeight: 1.65, color: '#334155' },
            '& .MuiInputBase-input::placeholder': { color: '#94A3B8', opacity: 1 },
          }}
        />

        {/* Footer */}
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: '0.68rem', color: '#CBD5E1', fontWeight: 500, mr: 'auto' }}>
            {noteText.length} / 2000
          </Typography>

          {(editMode || noteText.trim()) && (
            <Button
              size="small"
              variant="outlined"
              onClick={resetCompose}
              sx={{
                fontSize: '0.78rem',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.5,
                borderRadius: '8px',
                borderColor: '#E2E8F0',
                color: '#475569',
                '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
              }}
            >
              Cancel
            </Button>
          )}

          <Button
            size="small"
            variant="contained"
            onClick={handleSaveNote}
            disabled={!noteText.trim()}
            startIcon={<CheckIcon sx={{ fontSize: '0.8rem !important' }} />}
            sx={{
              fontSize: '0.78rem',
              textTransform: 'none',
              fontWeight: 700,
              px: 2,
              py: 0.5,
              borderRadius: '8px',
              bgcolor: '#10B981',
              boxShadow: '0 2px 8px rgba(16,185,129,0.35)',
              '&:hover': { bgcolor: '#059669' },
              '&.Mui-disabled': { bgcolor: '#10B981', opacity: 0.4, color: '#FFF' },
            }}
          >
            {editMode ? 'Update note' : 'Save note'}
          </Button>
        </Box>
      </Paper>

      {/* ── Notes list ── */}
      {showLoader ? (
        <LeadTabPanelLoader />
      ) : currentNotes && currentNotes.length > 0 ? (
        noteGroups.map((group) => (
          <Box key={group.dateKey}>
            <DateSeparator label={group.label} />
            {group.notes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </Box>
        ))
      ) : (
        <EmptyNotesCard
          onAddNote={() => {
            const el = document.getElementById('note-compose');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          title="No notes available for this lead"
          description="Add your first note to start tracking important information"
        />
      )}
    </Box>
  );
};

export default LeadNotes;
